import React from 'react';
import { Typography, styled, keyframes } from '@mui/material';
import AgentAvatar from './AgentAvatar';
import { Agent } from '../../stores/chatStore';

interface TypingIndicatorProps {
  agents: Agent[];
  typingAgentIds: string[];
}

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-10px);
  }
`;

const TypingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(1),
  maxWidth: '70%',
  marginRight: 'auto',
}));

const DotContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
  paddingLeft: theme.spacing(6),
}));

const Dot = styled('span')<{ delay: number }>(({ theme, delay }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: theme.palette.text.secondary,
  animation: `${bounce} 1.4s ease-in-out ${delay}s infinite`,
}));

const TypingIndicator: React.FC<TypingIndicatorProps> = ({ agents, typingAgentIds }) => {
  if (typingAgentIds.length === 0) return null;

  // Get the first typing agent for the avatar
  const typingAgent = agents.find(a => typingAgentIds.includes(a.id));

  // Get names of all typing agents
  const typingAgentNames = typingAgentIds
    .map(id => {
      const agent = agents.find(a => a.id === id);
      return agent ? (agent.custom_name || agent.name) : null;
    })
    .filter(Boolean);

  const typingText = typingAgentNames.length > 1
    ? `${typingAgentNames.join(', ')} are typing`
    : `${typingAgentNames[0]} is typing`;

  return (
    <TypingContainer>
      {typingAgent && (
        <div style={{ position: 'absolute' }}>
          <AgentAvatar agent={typingAgent} showStatus={false} />
        </div>
      )}
      <DotContainer>
        <Dot delay={0} />
        <Dot delay={0.2} />
        <Dot delay={0.4} />
      </DotContainer>
      <Typography variant="caption" color="text.secondary" style={{ marginLeft: 8 }}>
        {typingText}
      </Typography>
    </TypingContainer>
  );
};

export default TypingIndicator;
